import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { PILL_TONES, PILL_SIZES } from './tones';

// The one component in the app that knows what a pill looks like. Everything else goes through
// StatusPill / LabelPill / CountPill / MarkerPill, which only choose a role and a tone.
//
// Three questions decide every pill, and nothing else varies:
//   shape — what it means. A pill is a thing (tag, source, type, role); a rounded rectangle is
//           a state (task status, health, upload progress) or a count.
//   size  — where it sits. `inline` beside running text, `standalone` in its own cell or card
//           corner, `touch` in the field engineer views, which are read on a phone outdoors.
//   tone  — how urgent it is, from PILL_TONES. Status uses the full ramp; labels stay off it,
//           because a tag is never "good" and an Engineer is never a warning.
//
// A pill never has a border: an outline means a control (the Assign button in the tasks table,
// the Global toggle in the tag picker), and nothing static should look pressable.
export default function Pill({
  tone,
  toneClass,
  size,
  shape,
  icon,
  tabular,
  title,
  className,
  children,
  onRemove,
  removeLabel,
}) {
  return (
    <span
      className={classNames(
        'mntl-pill',
        toneClass || PILL_TONES[tone],
        size !== 'standalone' && `mntl-pill--${size}`,
        shape === 'pill' && 'mntl-pill--label',
        tabular && 'mntl-pill--count',
        className,
      )}
      title={title}
    >
      {icon && <i className={`bi ${icon}`} aria-hidden />}
      {children}
      {onRemove && (
        <button
          type="button"
          className="mntl-pill__remove"
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <i className="bi bi-x-lg" aria-hidden />
        </button>
      )}
    </span>
  );
}

Pill.propTypes = {
  /** A key of PILL_TONES. Ignored when `toneClass` is given. */
  tone: PropTypes.oneOf(Object.keys(PILL_TONES)),
  /** An explicit colour class pair, for the solid marker fills. */
  toneClass: PropTypes.string,
  size: PropTypes.oneOf(PILL_SIZES),
  shape: PropTypes.oneOf(['rect', 'pill']),
  icon: PropTypes.string,
  tabular: PropTypes.bool,
  title: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  onRemove: PropTypes.func,
  removeLabel: PropTypes.string,
};

Pill.defaultProps = {
  tone: 'neutral',
  toneClass: undefined,
  size: 'standalone',
  shape: 'rect',
  icon: undefined,
  tabular: false,
  title: undefined,
  className: '',
  children: null,
  onRemove: undefined,
  removeLabel: undefined,
};
